import React, { useState } from 'react';
import { authApi } from '../api';
import Logo from './Logo'; // Ton composant Logo
import { User, Lock, ArrowRight, UserPlus, AlertCircle, Loader2 } from 'lucide-react';

const Login = ({ onLoginSuccess }) => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    display_name: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isLoginMode) {
        // --- CONNEXION ---
        const response = await authApi.login({ 
            username: formData.username, 
            password: formData.password 
        });
        localStorage.setItem('token', response.data.access_token);
        onLoginSuccess();
      } else {
        // --- INSCRIPTION ---
        await authApi.register({
            username: formData.username,
            password: formData.password,
            display_name: formData.display_name
        });
        
        // Auto-login après inscription
        const loginResponse = await authApi.login({ 
            username: formData.username, 
            password: formData.password 
        });
        localStorage.setItem('token', loginResponse.data.access_token);
        onLoginSuccess();
      }
    } catch (err) {
      console.error(err);
      if (isLoginMode) {
        setError("Identifiants incorrects. Veuillez réessayer.");
      } else {
        // --- GESTION INTELLIGENTE DES ERREURS 422 (VALIDATION) ---
        const detail = err.response?.data?.detail;
        
        if (Array.isArray(detail)) {
          // Traduction des champs techniques en français
          const fieldMap = {
            username: "L'identifiant",
            display_name: "Le pseudo",
            password: "Le mot de passe"
          };

          const messages = detail.map(d => {
             // Récupère le nom du champ (ex: 'username')
             const fieldName = d.loc[d.loc.length - 1];
             const friendlyName = fieldMap[fieldName] || fieldName;
             
             // Traduction des messages courants de Pydantic
             let msg = d.msg;
             if (msg.includes("string does not match regex")) msg = "ne doit contenir que des lettres, chiffres ou _ (sans espaces).";
             if (msg.includes("at least")) msg = "est trop court.";
             if (msg.includes("at most")) msg = "est trop long.";
             
             return `${friendlyName} ${msg}`;
          }).join('. ');
          
          setError(messages);
        } else if (typeof detail === 'string') {
          setError(detail); // Erreur simple (ex: "Username already taken")
        } else {
          setError("Une erreur est survenue. Vérifiez vos données.");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 relative overflow-hidden">
      
      {/* Background blobs ... */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-600/20 rounded-full blur-[100px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-600/20 rounded-full blur-[100px]"></div>

      <div className="w-full max-w-md bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-8 relative z-10">
        
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="mb-6 scale-110">
             <Logo size="text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">
            {isLoginMode ? 'Bon retour !' : 'Rejoignez-nous'}
          </h2>
          <p className="text-gray-400 mt-2 text-sm">
            {isLoginMode ? 'Connectez-vous pour suivre vos animes' : 'Créez votre compte pour commencer'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2 text-red-400 text-sm animate-pulse">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          
          {/* IDENTIFIANT (Username) */}
          <div>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                <User size={18} />
              </div>
              <input 
                type="text" 
                name="username"
                placeholder="Identifiant" 
                required
                // VALIDATION HTML5 : Regex identique à Pydantic
                pattern="[a-zA-Z0-9_]{3,30}"
                title="3 à 30 caractères. Lettres, chiffres et tiret bas (_) uniquement."
                value={formData.username}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white placeholder-gray-500 transition-all outline-none"
              />
            </div>
            {/* Petit texte d'aide pour guider l'utilisateur */}
            {!isLoginMode && (
                <p className="text-[10px] text-gray-500 mt-1 ml-1">
                    Unique, sans espaces, lettres|chiffres|_
                </p>
            )}
          </div>

          {/* PSEUDO (Display Name) */}
          {!isLoginMode && (
             <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                  <UserPlus size={18} />
                </div>
                <input 
                  type="text" 
                  name="display_name"
                  placeholder="Pseudo affiché" 
                  required={!isLoginMode}
                  maxLength={20} // Limite Frontend
                  value={formData.display_name}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white placeholder-gray-500 transition-all outline-none"
                />
             </div>
          )}


          {/* MOT DE PASSE */}
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
              <Lock size={18} />
            </div>
            <input 
              type="password" 
              name="password"
              placeholder="Mot de passe" 
              required
              minLength={3} // Limite Frontend
              value={formData.password}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white placeholder-gray-500 transition-all outline-none"
            />
          </div>

          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold py-3 rounded-lg shadow-lg hover:shadow-blue-500/25 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
                <Loader2 size={20} className="animate-spin" />
            ) : (
                <>
                    {isLoginMode ? 'Se connecter' : "S'inscrire"}
                    <ArrowRight size={18} />
                </>
            )}
          </button>
        </form>

        {/* Footer / Toggle */}
        <div className="mt-8 text-center pt-6 border-t border-gray-700/50">
          <p className="text-gray-400 text-sm">
            {isLoginMode ? "Pas encore de compte ?" : "Déjà un compte ?"}
            <button 
              onClick={() => {
                  setIsLoginMode(!isLoginMode);
                  setError('');
              }}
              className="ml-2 text-blue-400 hover:text-blue-300 font-semibold hover:underline transition-all"
            >
              {isLoginMode ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Login;